import { WEEK_DAYS } from "../constants/suggestions";

// ─── localStorage ────────────────────────────────────
export function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

export function save(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ─── 日付 ────────────────────────────────────────────
export function getDayLabel(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return `${d.getMonth() + 1}/${d.getDate()}(${WEEK_DAYS[d.getDay()]})`;
}

export function getTodayIdx(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.getDay();
}

export function getTodayStr() {
  return new Date().toISOString().split("T")[0];
}

export function formatDateKey(dateInput = new Date()) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const LB_TO_KG = 0.453592;

const normalizeWorkoutWeightUnit = (unit) => {
  const value = String(unit || "kg").trim().toLowerCase();
  if (value === "lbs" || value === "lb" || value === "pound" || value === "pounds") return "lbs";
  if (value === "bw" || value === "bodyweight" || value === "自重") return "BW";
  return "kg";
};

const getWorkoutSetUnit = (set, fallbackUnit = "kg") =>
  normalizeWorkoutWeightUnit(
    set?.weightMode
    || set?.weightType
    || set?.displayUnit
    || set?.unit
    || set?.weightUnit
    || set?.weight_unit
    || fallbackUnit
  );

const getWorkoutSetDisplayUnit = (unit) => {
  const normalized = normalizeWorkoutWeightUnit(unit);
  return normalized === "lbs" ? "lb" : normalized;
};

const hasDisplayWeightValue = (set) => {
  const value = set?.displayWeight ?? set?.originalWeight ?? set?.inputWeight;
  return value !== undefined && value !== null && String(value).trim() !== "";
};

const getWorkoutSetDisplayWeight = (set, unit) => {
  const value = set?.displayWeight ?? set?.originalWeight ?? set?.inputWeight ?? set?.weight;
  if (normalizeWorkoutWeightUnit(unit) === "BW" || String(value || "").toUpperCase() === "BW") {
    return "BW";
  }
  return value;
};

export function sanitizeWorkoutSet(set, { allowBodyweight = true } = {}) {
  if (!set || typeof set !== "object") return null;

  const reps = Number(set.reps ?? set.rep);
  if (!Number.isFinite(reps) || reps <= 0) return null;

  const unit = getWorkoutSetUnit(set);
  const displayWeight = getWorkoutSetDisplayWeight(set, unit);
  const rawWeight = unit === "lbs" && hasDisplayWeightValue(set)
    ? displayWeight
    : set.weight ?? set.weightKg ?? set.weight_kg ?? set.kg ?? displayWeight;

  if (unit === "BW" || String(rawWeight).toUpperCase() === "BW") {
    if (!allowBodyweight) return null;
    return {
      ...set,
      weight: "BW",
      displayWeight: "BW",
      originalWeight: set.originalWeight ?? displayWeight ?? "BW",
      displayUnit: "BW",
      unit: "BW",
      weightMode: set.weightMode || "BW",
      weightType: set.weightType || "BW",
      weightUnit: set.weightUnit || "BW",
      weight_unit: set.weight_unit || "BW",
      reps,
      done: Boolean(set.done),
    };
  }

  const displayWeightNumber = Number(rawWeight);
  if (!Number.isFinite(displayWeightNumber) || displayWeightNumber <= 0) return null;

  const weightKg = unit === "lbs" ? displayWeightNumber * LB_TO_KG : displayWeightNumber;
  const displayUnit = getWorkoutSetDisplayUnit(unit);

  return {
    ...set,
    weight: weightKg,
    weightKg,
    normalizedWeightKg: weightKg,
    displayWeight: displayWeight ?? rawWeight,
    originalWeight: set.originalWeight ?? displayWeight ?? rawWeight,
    displayUnit,
    unit,
    weightMode: set.weightMode || unit,
    weightType: set.weightType || unit,
    weightUnit: set.weightUnit || unit,
    weight_unit: set.weight_unit || unit,
    reps,
    done: Boolean(set.done),
  };
}

export function sanitizeWorkoutSets(sets, options) {
  return (Array.isArray(sets) ? sets : [])
    .map((set) => sanitizeWorkoutSet(set, options))
    .filter(Boolean);
}

/**
 * Counts sets where reps >= 1, regardless of weight.
 * Single canonical definition used by all screens for display set counts.
 */
export function countValidSets(sets) {
  return (Array.isArray(sets) ? sets : []).reduce((sum, s) => {
    const reps = Number(s?.reps ?? s?.rep);
    return sum + (Number.isFinite(reps) && reps >= 1 ? 1 : 0);
  }, 0);
}

export function isCompletedWorkoutSet(set) {
  if (!set || typeof set !== "object") return false;

  const reps = Number(set.reps);
  if (!Number.isFinite(reps) || reps <= 0) return false;

  if (set.weight === "BW") {
    return true;
  }

  const weight = Number(set.weight);
  return Number.isFinite(weight) && weight > 0;
}

export function getRecordSourceSets(record) {
  if (!record || typeof record !== "object") return [];

  if (Array.isArray(record.sets) && record.sets.length > 0) {
    return record.sets;
  }

  return [{
    weight: record.weight ?? record.weightKg ?? record.weight_kg ?? record.kg ?? record.displayWeight,
    displayWeight: record.displayWeight ?? record.originalWeight ?? record.inputWeight ?? record.weight,
    originalWeight: record.originalWeight ?? record.displayWeight ?? record.weight,
    displayUnit: record.displayUnit ?? record.unit ?? record.weightUnit ?? record.weight_unit,
    unit: record.unit ?? record.weightMode ?? record.weightType ?? record.displayUnit ?? record.weightUnit ?? record.weight_unit,
    weightMode: record.weightMode ?? record.unit ?? record.displayUnit ?? record.weightUnit ?? record.weight_unit,
    weightType: record.weightType ?? record.unit ?? record.displayUnit ?? record.weightUnit ?? record.weight_unit,
    weightUnit: record.weightUnit ?? record.unit ?? record.displayUnit,
    weight_unit: record.weight_unit ?? record.unit ?? record.displayUnit,
    reps: record.reps ?? record.rep,
    done: record.done,
  }];
}

export function sanitizeHistoryRecord(record, { allowBodyweight = true } = {}) {
  if (!record || typeof record !== "object") return null;

  const sets = sanitizeWorkoutSets(getRecordSourceSets(record), { allowBodyweight });
  if (!sets.length) return null;

  const firstSet = sets[0];
  const weight = firstSet.weight === "BW" ? "BW" : Number(firstSet.weight);
  const reps = Number(firstSet.reps);
  const order = Number(record.order);
  const rawDate = record.date ?? record.workoutDate ?? record.workout_date;
  const date = String(rawDate || "").slice(0, 10);
  const bodyPart = String(record.bodyPart || record.body_part || "").trim();

  return {
    ...record,
    date,
    order: Number.isFinite(order) ? order : 999,
    bodyPart,
    sets,
    weight,
    reps,
  };
}

export function hasValidHistoryRecord(record, { allowBodyweight = true } = {}) {
  return Boolean(sanitizeHistoryRecord(record, { allowBodyweight })?.date);
}

export function hasValidWorkoutOnDate(historyMap, targetDate) {
  const normalizedDate = String(targetDate || "").trim();
  if (!normalizedDate) return false;

  return Object.values(historyMap || {}).some((records) =>
    (records || []).some((record) => {
      const sanitizedRecord = sanitizeHistoryRecord(record, { allowBodyweight: true });
      return sanitizedRecord?.date === normalizedDate;
    })
  );
}

export function getValidWorkoutDatesFromHistory(historyMap, { prefix = "", since = "" } = {}) {
  const validDates = new Set();

  Object.values(historyMap || {}).forEach((records) => {
    (records || []).forEach((record) => {
      const sanitizedRecord = sanitizeHistoryRecord(record, { allowBodyweight: true });
      const date = String(sanitizedRecord?.date || "").trim();
      if (!date) return;
      if (prefix && !date.startsWith(prefix)) return;
      if (since && date < since) return;
      validDates.add(date);
    });
  });

  return Array.from(validDates).sort();
}

export function buildHistoryRecordSignature(record) {
  const sanitizedRecord = sanitizeHistoryRecord(record, { allowBodyweight: true });
  if (!sanitizedRecord?.date) return "";

  const setsSignature = sanitizedRecord.sets
    .map((set) => {
      const unit = String(set.displayUnit || set.unit || set.weightUnit || set.weight_unit || "").trim();
      const displayWeight = String(set.displayWeight ?? "").trim();
      return `${set.weight === "BW" ? "BW" : Number(set.weight)}|${displayWeight}|${unit}|${Number(set.reps)}|${set.done ? 1 : 0}`;
    })
    .join(";");

  return `${sanitizedRecord.date}::${sanitizedRecord.bodyPart || ""}::${setsSignature}`;
}

const getHistoryRecordDurationSec = (record) => {
  const candidates = [
    Number(record?.duration_sec),
    Number(record?.durationSec),
    Number(record?.durationMinutes) * 60,
    Number(record?.elapsedMinutes) * 60,
  ];

  const durationSec = candidates.find((value) => Number.isFinite(value) && value > 0);
  return durationSec ? Math.floor(durationSec) : 0;
};

const getHistoryRecordMetrics = (record) => {
  const sanitizedRecord = sanitizeHistoryRecord(record, { allowBodyweight: true });
  const sets = sanitizedRecord?.sets || [];
  const volume = sets.reduce((sum, set) => {
    const weight = Number(set?.weight);
    const reps = Number(set?.reps);
    if (!Number.isFinite(weight) || !Number.isFinite(reps)) return sum;
    return sum + (weight > 0 && reps > 0 ? weight * reps : 0);
  }, 0);

  return {
    setCount: sets.length,
    volume,
  };
};

const getHistoryRecordUpdatedMs = (record) => {
  const candidates = [
    record?.updated_at,
    record?.updatedAt,
    record?.remoteUpdatedAt,
    record?.rowUpdatedAt,
    record?.savedAt,
  ];

  for (const candidate of candidates) {
    const ms = Date.parse(candidate);
    if (Number.isFinite(ms)) return ms;
  }

  return 0;
};

const getHistoryRecordScopeRank = (record) => {
  const sourceText = String(
    record?.historyScope
    || record?.sourceScope
    || record?.source
    || record?.sourceTable
    || record?.recordSource
    || ""
  ).toLowerCase();

  if (record?.exactHistory === true || sourceText.includes("exact") || sourceText.includes("workouts.data")) return 3;
  if (record?.legacyHistory === true || sourceText.includes("legacy")) return 1;
  if (sourceText.includes("summary_json") || sourceText.includes("workout_session") || sourceText.includes("cache")) return 0;
  return 2;
};

const choosePreferredHistoryRecord = (existingRecord, incomingRecord) => {
  if (!existingRecord) return incomingRecord;

  const existingSignature = buildHistoryRecordSignature(existingRecord);
  const incomingSignature = buildHistoryRecordSignature(incomingRecord);

  if (!existingSignature) return incomingRecord;
  if (!incomingSignature) return existingRecord;
  if (existingSignature === incomingSignature) {
    const existingDurationSec = getHistoryRecordDurationSec(existingRecord);
    const incomingDurationSec = getHistoryRecordDurationSec(incomingRecord);
    const durationSec = Math.max(existingDurationSec, incomingDurationSec);
    if (durationSec <= 0) return existingRecord;
    const durationMinutes = Math.max(1, Math.round(durationSec / 60));
    return {
      ...existingRecord,
      duration_sec: durationSec,
      durationSec,
      durationMinutes,
      elapsedMinutes: durationMinutes,
    };
  }

  const existingMetrics = getHistoryRecordMetrics(existingRecord);
  const incomingMetrics = getHistoryRecordMetrics(incomingRecord);
  const existingScopeRank = getHistoryRecordScopeRank(existingRecord);
  const incomingScopeRank = getHistoryRecordScopeRank(incomingRecord);

  if (incomingScopeRank !== existingScopeRank) {
    return incomingScopeRank > existingScopeRank ? incomingRecord : existingRecord;
  }

  if (incomingMetrics.setCount !== existingMetrics.setCount) {
    return incomingMetrics.setCount > existingMetrics.setCount ? incomingRecord : existingRecord;
  }

  const existingUpdatedMs = getHistoryRecordUpdatedMs(existingRecord);
  const incomingUpdatedMs = getHistoryRecordUpdatedMs(incomingRecord);
  if (existingUpdatedMs !== incomingUpdatedMs && (existingUpdatedMs > 0 || incomingUpdatedMs > 0)) {
    return incomingUpdatedMs >= existingUpdatedMs ? incomingRecord : existingRecord;
  }

  return incomingRecord;
};

export function mergeHistoryMaps(...sources) {
  const mergedMaps = new Map();

  sources.forEach((source) => {
    if (!isPlainObject(source)) return;

    Object.entries(source).forEach(([exerciseName, records]) => {
      if (!exerciseName || !Array.isArray(records) || !records.length) return;

      const dateMap = mergedMaps.get(exerciseName) || new Map();

      records.forEach((record) => {
        const sanitizedRecord = sanitizeHistoryRecord(record, { allowBodyweight: true });
        if (!sanitizedRecord?.date) return;

        const existingRecord = dateMap.get(sanitizedRecord.date);
        dateMap.set(
          sanitizedRecord.date,
          choosePreferredHistoryRecord(existingRecord, sanitizedRecord)
        );
      });

      if (dateMap.size > 0) {
        mergedMaps.set(exerciseName, dateMap);
      }
    });
  });

  const mergedHistory = {};

  mergedMaps.forEach((dateMap, exerciseName) => {
    const records = Array.from(dateMap.values())
      .filter(Boolean)
      .sort((a, b) => {
        const dateCompare = String(a?.date || "").localeCompare(String(b?.date || ""));
        if (dateCompare !== 0) return dateCompare;

        const orderA = Number.isFinite(a?.order) ? a.order : 999;
        const orderB = Number.isFinite(b?.order) ? b.order : 999;
        return orderA - orderB;
      });

    if (records.length > 0) {
      mergedHistory[exerciseName] = records;
    }
  });

  return mergedHistory;
}

const getWorkoutRowHistoryData = (row) => {
  if (isPlainObject(row?.data?.history)) return row.data.history;
  if (isPlainObject(row?.data?.records)) return row.data.records;
  return row?.data;
};

const addWorkoutRowRecordToHistory = (historyMap, exerciseName, record, fallbackDate, scope = "", rowMeta = {}) => {
  if (!exerciseName || !isPlainObject(record)) return;
  const recordDate = String(record?.date || record?.workoutDate || record?.workout_date || "").slice(0, 10);
  const resolvedDate = recordDate || fallbackDate;
  const nextRecord = recordDate || !fallbackDate
    ? record
    : {
        ...record,
        date: fallbackDate,
        workoutDate: fallbackDate,
        workout_date: fallbackDate,
      };
  const historyScope = scope || "";
  const scopedRecord = {
    ...nextRecord,
    ...(resolvedDate
      ? {
          date: resolvedDate,
          workoutDate: resolvedDate,
          workout_date: resolvedDate,
        }
      : {}),
    ...(historyScope
      ? {
          historyScope,
          sourceScope: historyScope,
          recordSource: `workouts.data.${historyScope}`,
          sourceTable: "workouts",
          exactHistory: historyScope === "exact",
          legacyHistory: historyScope === "legacy",
        }
      : {}),
    ...(rowMeta.updatedAt
      ? {
          rowUpdatedAt: rowMeta.updatedAt,
          remoteUpdatedAt: rowMeta.updatedAt,
          updated_at: nextRecord?.updated_at || rowMeta.updatedAt,
        }
      : {}),
    ...(rowMeta.rowDate ? { rowDate: rowMeta.rowDate } : {}),
    ...(rowMeta.rowId ? { rowId: rowMeta.rowId } : {}),
  };

  if (!historyMap[exerciseName]) historyMap[exerciseName] = [];
  historyMap[exerciseName].push(scopedRecord);
};

const removeHistoryDateFromMap = (historyMap, targetDate) => {
  const normalizedDate = String(targetDate || "").slice(0, 10);
  if (!normalizedDate) return historyMap || {};

  const next = {};
  Object.entries(historyMap || {}).forEach(([exerciseName, records]) => {
    const keptRecords = (records || []).filter((record) => (
      String(record?.date || record?.workoutDate || record?.workout_date || "").slice(0, 10) !== normalizedDate
    ));
    if (keptRecords.length > 0) next[exerciseName] = keptRecords;
  });
  return next;
};

const applyPreferredHistoryDates = (baseHistory, preferredHistory, dates = []) => {
  const normalizedDates = [...new Set(
    (dates || []).map((date) => String(date || "").slice(0, 10)).filter(Boolean)
  )];
  let nextHistory = mergeHistoryMaps(baseHistory || {});

  normalizedDates.forEach((date) => {
    nextHistory = removeHistoryDateFromMap(nextHistory, date);
    const dateRecords = {};
    Object.entries(preferredHistory || {}).forEach(([exerciseName, records]) => {
      const filtered = (records || []).filter((record) => (
        String(record?.date || record?.workoutDate || record?.workout_date || "").slice(0, 10) === date
      ));
      if (filtered.length > 0) dateRecords[exerciseName] = filtered;
    });
    nextHistory = mergeHistoryMaps(nextHistory, dateRecords);
  });

  return nextHistory;
};

export function buildHistoryFromWorkoutRowsWithScopes(rows) {
  const sortedRows = [...(rows || [])]
    .filter((row) => isPlainObject(row?.data))
    .sort((a, b) => String(a?.date || "").localeCompare(String(b?.date || "")));

  const exactRowHistories = [];
  const legacyEmbeddedHistories = [];

  sortedRows.forEach((row) => {
    const rowDate = String(row?.date || row?.workout_date || row?.workoutDate || "").slice(0, 10);
    const rowMeta = {
      rowDate,
      rowId: row?.id || null,
      updatedAt: row?.updated_at || row?.updatedAt || row?.created_at || row?.createdAt || null,
    };
    const rowHistoryData = getWorkoutRowHistoryData(row);
    const exactHistory = {};
    const legacyHistory = {};

    Object.entries(rowHistoryData || {}).forEach(([exerciseName, rawRecords]) => {
      const records = Array.isArray(rawRecords)
        ? rawRecords
        : isPlainObject(rawRecords)
          ? [rawRecords]
          : [];

      records.forEach((record) => {
        const recordDate = String(record?.date || record?.workoutDate || record?.workout_date || "").slice(0, 10);
        const effectiveDate = recordDate || rowDate;
        if (!effectiveDate) return;

        // Each Supabase row is upserted with ON CONFLICT (user_id, date), so every
        // record inside a row belongs to that row's date.  A mismatch between
        // record.date and row.date means the embedded date is stale (e.g. written by
        // the stale-closure double-write bug).  In that case we trust row.date and put
        // the record in exactHistory so it displays on the correct day.
        // Legacy multi-date rows (older data formats) are preserved: if there is NO
        // dedicated Supabase row for recordDate AND recordDate < rowDate, we still fall
        // back to legacyHistory so the older exercise history isn't lost.
        if (rowDate && effectiveDate !== rowDate) {
          // Put in legacyHistory — will be overridden by exactHistory for any date
          // that has its own dedicated row.
          addWorkoutRowRecordToHistory(legacyHistory, exerciseName, record, effectiveDate, "legacy", rowMeta);
          // Also add to exactHistory using rowDate so this row's content is always
          // visible on the correct date even if record.date is wrong.
          addWorkoutRowRecordToHistory(exactHistory, exerciseName, record, rowDate, "exact", rowMeta);
        } else {
          addWorkoutRowRecordToHistory(exactHistory, exerciseName, record, rowDate || effectiveDate, "exact", rowMeta);
        }
      });
    });

    exactRowHistories.push(exactHistory);
    legacyEmbeddedHistories.push(legacyHistory);
  });

  const exactHistory = mergeHistoryMaps(...exactRowHistories);
  const legacyHistory = mergeHistoryMaps(...legacyEmbeddedHistories);
  const exactDates = getValidWorkoutDatesFromHistory(exactHistory);
  const history = applyPreferredHistoryDates(legacyHistory, exactHistory, exactDates);

  return {
    history,
    exactHistory,
    legacyHistory,
    exactDates,
    legacyDates: getValidWorkoutDatesFromHistory(legacyHistory),
  };
}

export function buildHistoryFromWorkoutRows(rows) {
  return buildHistoryFromWorkoutRowsWithScopes(rows).history;
}

export const PR_UPDATE_TOLERANCE_KG = 0.15;

export function getBestRmSet(sets, { allowBodyweight = false, bodyWeightKg = null } = {}) {
  const effectiveAllowBodyweight = allowBodyweight || bodyWeightKg != null;
  const validSets = sanitizeWorkoutSets(sets, { allowBodyweight: effectiveAllowBodyweight });
  if (!validSets.length) return null;

  return validSets.reduce((best, set) => {
    const rm = calc1RM([set], { bodyWeightKg });
    if (!best || rm > best.rm) {
      return {
        ...set,
        rm,
      };
    }
    return best;
  }, null);
}

export function hasMeaningfulPRIncrease(currentSets, previousSets, previousRM = null, tolerance = PR_UPDATE_TOLERANCE_KG, { bodyWeightKg = null } = {}) {
  const currentTopSet = getBestRmSet(currentSets, { allowBodyweight: true, bodyWeightKg });
  const previousTopSet = getBestRmSet(previousSets, { allowBodyweight: true, bodyWeightKg });

  if (!currentTopSet || !previousTopSet) return false;

  if (
    Number(currentTopSet.weight) === Number(previousTopSet.weight) &&
    Number(currentTopSet.reps) === Number(previousTopSet.reps)
  ) {
    return false;
  }

  const baselineRM = Number.isFinite(previousRM) && previousRM > 0
    ? previousRM
    : previousTopSet.rm;

  return currentTopSet.rm - baselineRM > tolerance;
}

// ─── added_weight_kg の意味と読み取りルール ──────────────
// added_weight_kg は weight === "BW" のときのみ有効（懸垂+10kg ベストなど）。
// ただし数値モードに切り替えても値がセットオブジェクトに残る仕様になっている
// （ユーザーが BW に戻したときに入力値を復元するため）。
// そのため weight が数値のセットに added_weight_kg が入っていることがある。
// この関数以外で added_weight_kg を直接読まないこと。
// 読む必要がある場合は必ず weight === "BW" であることを確認してから参照すること。
//
// ─── BW セットの表示ラベル ────────────────────────────────
// set.weight === "BW" かつ added_weight_kg > 0 → "自重+Xkg"
// set.weight === "BW" かつ added_weight_kg なし/0/負  → "自重"
// set.weight !== "BW" → null（呼び出し元が数値フォーマットを担当）
//
// added_weight_kg は formatKg で小数1桁に丸める。
// ユーザー直接入力（lb→kg 変換経路なし）だが、UI 表示の一貫性のため丸める。
// 計算には resolveEffectiveWeightKg が生値を使うため、この丸めは計算に影響しない。
export function formatBWSetLabel(set) {
  if (set?.weight !== "BW") return null;
  const added = Number(set?.added_weight_kg);
  return added > 0 ? `自重+${formatKg(added)}kg` : "自重";
}

// ─── kg 値を小数1桁に丸めて文字列化 ─────────────────────
// lb→kg 変換後の端数（例: 73.481904kg）が UI や AI プロンプトに漏れるのを防ぐ。
// 計算（1RM・ボリューム）は生値を使い、この関数は文字列化の直前だけで呼ぶ。
// 以前散在していた重複実装（HistoryExerciseItem.fmtKg / WorkoutSessionShareModal.formatWeightValue /
// FeedSection.formatSummaryItemSetsText インライン / useFriendsData.formatSessionSetDisplay インライン）
// は全てこの関数に統合済み。
export const formatKg = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0";
  return String(Math.round(num * 10) / 10);
};

// ─── AI コンテキスト用の PR 重量ラベル ───────────────────
// pr = { weight, added_weight_kg, effectiveKg, ... }
// BW セットは formatBWSetLabel 経由で生成し、effectiveKg があれば (Xkg) を付加する。
// useAI.js の2箇所（formatBig3MatchedExercise / buildExerciseHistoryContextTextFromStats）
// が共通して使う。純粋関数として helpers に置くことでテスト可能にする。
export function buildPrWeightLabel(pr) {
  if (pr?.weight === "BW") {
    const bwLabel = formatBWSetLabel(pr) ?? "自重";
    return pr.effectiveKg != null ? `${bwLabel}(${formatKg(pr.effectiveKg)}kg)` : bwLabel;
  }
  return `${formatKg(pr?.weight)}kg`;
}

// ─── 自重セットの実効重量を解決 ──────────────────────────
// "BW" セット: set.body_weight_kg → bodyWeightKg の順で解決。有効値なければ null
// 数値セット: Number(set.weight) が正有限値なら返す。無効なら null
export function resolveEffectiveWeightKg(set, bodyWeightKg = null) {
  if (set?.weight === "BW") {
    const bw = set.body_weight_kg ?? bodyWeightKg;
    if (bw == null || bw <= 0) return null;
    const added = Number(set.added_weight_kg);
    return bw + (Number.isFinite(added) && added > 0 ? added : 0);
  }
  const weight = Number(set?.weight);
  return (Number.isFinite(weight) && weight > 0) ? weight : null;
}

// ─── 推定1RM計算（Epley式）────────────────────────────

/**
 * Epley 式で推定 1RM を計算する低レベル純粋関数。
 * 丸めは行わない（表示側の責務）。
 *
 * @param {number} weightKg - 重量（kg）。BW 解決済みであること
 * @param {number} reps     - 回数
 * @returns {number} 推定 1RM（kg）。不正値のとき 0
 */
export function estimate1RM(weightKg, reps) {
  const w = Number(weightKg);
  const r = Number(reps);
  if (!Number.isFinite(w) || w <= 0) return 0;
  if (!Number.isFinite(r) || r <= 0) return 0;
  if (r === 1) return w;
  return w * (1 + r / 30);
}

export function calc1RM(sets, { bodyWeightKg = null } = {}) {
  const validSets = sanitizeWorkoutSets(sets, { allowBodyweight: true });
  if (!validSets.length) return 0;

  return Math.max(...validSets.map((s) => {
    let weight = Number(s.weight);
    const reps = Number(s.reps);
    if (!Number.isFinite(reps) || reps <= 0) return 0;
    if (!Number.isFinite(weight) || weight <= 0) {
      if (s.weight === "BW") {
        const effective = resolveEffectiveWeightKg(s, bodyWeightKg);
        if (effective === null) return 0;
        weight = effective;
      } else {
        return 0;
      }
    }
    return estimate1RM(weight, reps);
  }));
}

// ─── 単位変換 ─────────────────────────────────────────
export const KG_TO_LBS = 2.2046;

/** kg で保存されている値を表示用に変換 */
export function dispW(kg, unit) {
  if (kg === "BW") return "自重";
  if (kg === "" || kg === undefined || kg === null) return "";
  const n = Number(kg);
  if (isNaN(n) || n === 0) return unit === "lbs" ? "" : String(n === 0 ? "" : n);
  if (unit === "lbs") {
    return String(Math.round(n * KG_TO_LBS * 10) / 10);
  }
  // kg: 小数点以下1桁に丸める（例: 49.89512 → 49.9、50.0 → 50）
  const rounded = Math.round(n * 10) / 10;
  return String(rounded);
}

/** ユーザー入力（表示単位）を kg に変換して保存 */
export function storeW(val, unit) {
  if (val === "" || val === undefined || val === null) return val;
  if (val === "BW") return "BW";
  const n = Number(val);
  if (isNaN(n) || unit !== "lbs") return val;
  return String(Math.round(n / KG_TO_LBS * 100) / 100);
}
